import { useEffect, useId, useRef, useState } from 'react';
import api from '@/lib/api';
import { verifyToken } from '@/lib/jwtUtils';
import { Button } from '@/components/ui/button';

type Membership = { tenantId: string; institutionName: string; roles: string[]; enabled?: boolean };

function studentInstitutions(data: unknown): Membership[] {
  if (!Array.isArray(data) || data.some(item => !item
    || typeof item.tenantId !== 'string' || !item.tenantId.trim()
    || typeof item.institutionName !== 'string' || !item.institutionName.trim()
    || !Array.isArray(item.roles) || item.roles.some((role: unknown) => typeof role !== 'string')
    || (item.enabled !== undefined && typeof item.enabled !== 'boolean'))) {
    throw new Error('Invalid institution response');
  }
  return [...new Map(data.filter(item => item.enabled !== false && item.roles.includes('ROLE_STUDENT'))
    .map((item: Membership) => [item.tenantId, item])).values()];
}

export default function InstitutionSwitcher() {
  const [institutions, setInstitutions] = useState<Membership[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [switchError, setSwitchError] = useState('');
  const pending = useRef(false);
  const mounted = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true); setSwitchError('');
    try {
      const { data } = await api.get('/auth/institutions');
      const choices = studentInstitutions(data);
      if (mounted.current) { setInstitutions(choices); setLoadError(''); }
    } catch {
      if (mounted.current) {
        setInstitutions(null);
        setLoadError('Unable to load institutions. Your current session has been kept.');
      }
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, []);

  const change = async (tenantId: string) => {
    if (pending.current || !institutions?.some(item => item.tenantId === tenantId)) return;
    pending.current = true;
    setBusy(true); setSwitchError('');
    let navigating = false;
    try {
      const { data } = await api.post('/auth/institutions/switch', { tenantId });
      if (typeof data?.token !== 'string' || !data.token.trim()
        || !Array.isArray(data.roles) || !data.roles.includes('ROLE_STUDENT')) throw new Error('Portal access unavailable');
      if (!mounted.current) return;
      localStorage.removeItem('tenantCode');
      localStorage.setItem('token', data.token);
      localStorage.setItem('isFirstTimeLogin', data.isFirstTimeLogin ? 'true' : 'false');
      // Reload all tenant state only after a confirmed, Student-accessible switch.
      navigating = true;
      window.location.assign('/');
    } catch {
      navigating = false;
      if (mounted.current) setSwitchError('Unable to switch institutions. Your current session has been kept.');
    } finally {
      if (!navigating) {
        pending.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };
  const close = () => { setOpen(false); setSwitchError(''); trigger.current?.focus(); };
  const canSwitch = (institutions?.length ?? 0) > 1;
  let tenantId: string | undefined;
  try { tenantId = verifyToken(localStorage.getItem('token') || '')?.tenantId; } catch { /* Display only; the server still authorizes all access. */ }
  const current = institutions?.find(item => item.tenantId === tenantId);
  if (!canSwitch && !loadError) return null;

  return <div className="relative mt-2 flex min-w-0 flex-wrap items-center gap-2 text-inherit" aria-busy={busy} onKeyDown={event => {
    if (open && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    {canSwitch && <p className="min-w-0 flex-1 truncate text-sm text-inherit" title={current?.institutionName}>
      <span className="sr-only">Current institution: </span>{current?.institutionName || 'Current institution unavailable'}
    </p>}
    {canSwitch ? <Button ref={trigger} variant="outline" size="sm" aria-expanded={open} aria-controls={panelId}
      disabled={busy} onClick={() => open ? close() : setOpen(true)}>Switch institution</Button>
      : loadError ? <Button variant="outline" size="sm" disabled={busy} onClick={load}>Retry institution access</Button> : null}
    {loadError && <p role="alert" className="absolute right-0 top-full z-50 mt-2 w-64 max-w-[85vw] rounded-md border bg-white p-3 text-sm text-red-700 shadow-lg">{loadError}</p>}
    {open && canSwitch && <section id={panelId} aria-label="Switch institution" className="absolute right-0 top-full z-50 mt-2 w-64 max-w-[85vw] space-y-2 rounded-md border bg-white p-4 text-gray-900 shadow-lg">
      {switchError && <div role="alert" className="space-y-2">
        <p className="text-sm text-red-700">{switchError}</p>
        <Button variant="outline" disabled={busy} onClick={load}>Retry institutions</Button>
      </div>}
      <p className="text-sm">Switching leaves this page. Save your work first.</p>
      {institutions?.map(item => <Button key={item.tenantId} variant="outline" className="w-full whitespace-normal" disabled={busy || item.tenantId === tenantId} aria-current={item.tenantId === tenantId ? "true" : undefined} onClick={() => change(item.tenantId)}>{item.institutionName}{item.tenantId === tenantId ? " (Current)" : ""}</Button>)}
      <Button variant="ghost" disabled={busy} onClick={close}>Cancel</Button>
    </section>}
  </div>;
}
